/**
 * audio/combat-overlay.ts — combat-overlay music bed state machine (v4M1.7).
 *
 * The combat-overlay music bed sits on top of the always-on ambient bed
 * during combat activity and fades out when things go quiet. Driven by
 * the per-tick CombatEvent buffer that `advance-combat.ts` populates —
 * the same buffer the floating-damage-numbers VFX consumes — so a tick
 * with any combat activity counts as "in combat" regardless of which
 * weapon fired or who took damage.
 *
 * State machine:
 *
 *   silent ──(combat event seen)──▶ engaged
 *     ▲                                │
 *     │                                │ (no events for `cooldownTicks`)
 *     │                                ▼
 *     └────────────────────── disengaging
 *                          (fade out, then silent)
 *
 * Intensity ramps from 0 to 1 over `engageRampSec` when entering
 * `engaged`, and from current value back to 0 over `disengageRampSec`
 * when entering `disengaging`. Re-engaging mid-disengage cancels the
 * fade-out and ramps back to full intensity — no annoying "the music
 * died for half a second then came back" beat.
 *
 * Decoupled from the renderer / sim; consumes only `GameState.tick`
 * and `GameState.combatEvents`. The `AudioEngine` is the only side-
 * effect target: `startMusicBed` / `stopMusicBed` / `setMusicBedIntensity`.
 *
 * Per-tick, not per-frame: the state machine reads `state.tick` and
 * counts ticks since the last event, not wall-clock milliseconds, so
 * paused / fast-forwarded games behave consistently with the player's
 * sim-time intuition.
 */

import type { GameState } from '../state/types/index';
import type { AudioEngine } from './engine';

/**
 * Active state of the combat-overlay bed. Held by the session render
 * loop and passed back into `updateCombatOverlay` each frame.
 */
export interface CombatOverlayState {
  /** `silent` — no overlay; `engaged` — overlay running; `disengaging` — fading out. */
  phase: 'silent' | 'engaged' | 'disengaging';
  /**
   * Last tick we saw any combat event. Used to decide when to switch
   * `engaged` → `disengaging` based on `cooldownTicks` of quiet.
   */
  lastCombatTick: number;
  /**
   * Tick we observed at the previous frame so we can detect ticks
   * advancing. While the sim is paused, `state.tick` doesn't move and
   * we should neither engage nor disengage — combat events from the
   * last unpaused tick would otherwise count as "still happening".
   */
  prevObservedTick: number;
}

/**
 * Construct a fresh state. Cheap; no allocations beyond the object
 * itself. Caller owns the state — typically held in `session.ts`.
 */
export function createCombatOverlayState(): CombatOverlayState {
  return {
    phase: 'silent',
    lastCombatTick: -1,
    prevObservedTick: -1,
  };
}

/**
 * v4M1.7 — tuning constants. `cooldownTicks` chosen so a brief lull
 * mid-battle doesn't kill the music; `engageRampSec` is short enough
 * that the overlay reads as immediate; `disengageRampSec` is longer
 * so the music doesn't feel like it slammed off.
 */
const COMBAT_COOLDOWN_TICKS = 30; // 3 s of quiet at 10 Hz
const ENGAGE_RAMP_SEC = 0.8;
const DISENGAGE_RAMP_SEC = 3.0;
/**
 * Stop the bed entirely after the disengage fade-out fully completes.
 * Buys a small margin past the fade so we don't stop mid-ramp.
 */
const DISENGAGE_STOP_TICKS = Math.ceil((DISENGAGE_RAMP_SEC + 0.5) * 10);

/**
 * v4M1.7 — per-frame entry point. Reads `state.tick` and
 * `state.combatEvents`, advances the state machine, and calls into
 * the `AudioEngine` to start / stop / ramp the combat-overlay bed.
 *
 * Combat-event detection is "any entry in `state.combatEvents` this
 * tick". The buffer is per-tick transient (cleared at the start of
 * `advanceCombat`), so observing a non-empty buffer means activity
 * this tick. No need to diff against a prior snapshot.
 *
 * Pause-aware: when `state.tick` hasn't advanced since the previous
 * frame, the state machine doesn't accumulate cooldown ticks — a
 * paused game stays in its current phase indefinitely.
 */
export function updateCombatOverlay(
  state: GameState,
  engine: AudioEngine,
  stateOut: CombatOverlayState,
): void {
  const tick = state.tick;
  if (tick === stateOut.prevObservedTick) return; // paused; nothing to do
  stateOut.prevObservedTick = tick;

  const hasCombatThisTick = state.combatEvents.length > 0;
  if (hasCombatThisTick) {
    stateOut.lastCombatTick = tick;
  }

  switch (stateOut.phase) {
    case 'silent': {
      if (hasCombatThisTick) {
        // Enter engaged. Start the bed at near-zero, then ramp up to
        // full intensity via setMusicBedIntensity. The bed itself
        // ramps in via its authored fade, but starting it muted lets
        // setMusicBedIntensity drive the engaged ramp authoritatively.
        engine.startMusicBed('combat-overlay');
        engine.setMusicBedIntensity('combat-overlay', 0, 0);
        engine.setMusicBedIntensity('combat-overlay', 1, ENGAGE_RAMP_SEC);
        stateOut.phase = 'engaged';
      }
      return;
    }
    case 'engaged': {
      const ticksSinceCombat = tick - stateOut.lastCombatTick;
      if (ticksSinceCombat >= COMBAT_COOLDOWN_TICKS) {
        // Cooled down. Begin the disengage ramp.
        engine.setMusicBedIntensity('combat-overlay', 0, DISENGAGE_RAMP_SEC);
        stateOut.phase = 'disengaging';
      }
      return;
    }
    case 'disengaging': {
      if (hasCombatThisTick) {
        // Re-engage. Cancel the fade-out by ramping back to full
        // intensity. The bed is still running so no startMusicBed call.
        engine.setMusicBedIntensity('combat-overlay', 1, ENGAGE_RAMP_SEC);
        stateOut.phase = 'engaged';
        return;
      }
      const ticksSinceCombat = tick - stateOut.lastCombatTick;
      if (ticksSinceCombat >= COMBAT_COOLDOWN_TICKS + DISENGAGE_STOP_TICKS) {
        // Fade fully completed. Stop the bed cleanly; next combat
        // event re-enters `silent → engaged` via startMusicBed.
        engine.stopMusicBed('combat-overlay', 0.5);
        stateOut.phase = 'silent';
      }
      return;
    }
  }
}

/**
 * v4M1.7 — teardown helper. Called by `session.destroy` so a
 * Return-to-Main-Menu while combat-overlay is engaged doesn't leave
 * a stale bed running across the session boundary. The ambient bed
 * has its own teardown via `stopAmbientBed`; this is the equivalent
 * for the overlay.
 */
export function tearDownCombatOverlay(
  engine: AudioEngine,
  stateOut: CombatOverlayState,
): void {
  if (stateOut.phase !== 'silent') {
    engine.stopMusicBed('combat-overlay', 0.3);
    stateOut.phase = 'silent';
  }
}
